import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Search, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/** Botón "Buscar datos por patente" — consulta un proveedor externo
 * (configurable por env, ej. patente.ar) para traer SOLO marca/modelo/año.
 * Nunca datos del titular. Si no hay proveedor configurado o falla, avisa
 * y el campo sigue 100% editable a mano — nunca bloquea el ingreso. */
export function PlateLookup({ plate, onResolved, testIdPrefix = "plate" }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const currentPlateRef = useRef(plate);
  currentPlateRef.current = plate;

  useEffect(() => { setStatus(null); }, [plate]);

  const lookup = async () => {
    const clean = (plate || "").replace(/[\s-]/g, "").toUpperCase();
    if (clean.length < 6) {
      setStatus({ ok: false, msg: "Ingresá la patente completa primero" });
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const r = await api.get(`/vehicle-data/plate/${clean}`);
      const isStale = (currentPlateRef.current || "").replace(/[\s-]/g, "").toUpperCase() !== clean;
      if (isStale) return;
      if (r.data?.found) {
        onResolved({ brand: r.data.brand || "", model: r.data.model || "", year: r.data.year || "" });
        setStatus({ ok: true, msg: "Datos encontrados · revisá y confirmá" });
      } else {
        setStatus({ ok: false, msg: "No se pudo obtener el dato, completá manualmente" });
      }
    } catch (e) {
      const isStale = (currentPlateRef.current || "").replace(/[\s-]/g, "").toUpperCase() !== clean;
      if (!isStale) setStatus({ ok: false, msg: "No se pudo consultar, completá manualmente" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "2px 0 6px" }}>
      <button
        type="button"
        onClick={lookup}
        disabled={loading}
        data-testid={`${testIdPrefix}-plate-lookup-btn`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
          padding: "8px 14px", border: "1.5px solid #BF3437",
          background: loading ? "var(--invict-bone-2)" : "#fff", color: "#BF3437",
          fontWeight: 800, fontSize: 11, borderRadius: 6, cursor: loading ? "wait" : "pointer",
          textTransform: "uppercase", letterSpacing: "0.04em",
        }}
      >
        {loading ? <Loader2 size={13} className="dni-spin" /> : <Search size={13} />}
        {loading ? "Buscando..." : "Buscar datos por patente"}
      </button>
      {status && (
        <span
          data-testid={`${testIdPrefix}-plate-lookup-status`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
            color: status.ok ? "#16a34a" : "var(--invict-text-muted)",
          }}
        >
          {status.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {status.msg}
        </span>
      )}
    </div>
  );
}
